import React, { useState, useEffect, useCallback, useRef } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import MapView from '../components/MapView';
import Scoreboard from '../components/Scoreboard';
import TimelineAnimation, { TIMELINE_LENGTH } from '../components/TimelineAnimation';
import { stopsApi, scoreboardApi, settingsApi } from '../services/api';
import { getPlayer, clearPlayer } from '../utils/playerStorage';

function formatCountdown(totalSeconds) {
  if (totalSeconds <= 0) return '00:00:00';
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

function effectivePoints(basePoints, priorCompletions) {
  if (priorCompletions === 0) return basePoints;
  if (priorCompletions === 1) return Math.floor(basePoints * 0.7);
  return Math.floor(basePoints * 0.5);
}

function haversineMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function MapPage() {
  const [stops, setStops] = useState([]);
  const [scores, setScores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStop, setSelectedStop] = useState(null);
  const [userPosition, setUserPosition] = useState(null);
  const [geoStatus, setGeoStatus] = useState('pending'); // 'pending' | 'granted' | 'denied' | 'unavailable'
  const [endTime, setEndTime] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [stopRadius, setStopRadius] = useState(70);
  const [hidePoints, setHidePoints] = useState(false);
  const [hideScoreboard, setHideScoreboard] = useState(false);
  const [goalStopId, setGoalStopId] = useState(null);
  const [showAnimation, setShowAnimation] = useState(false);
  const [animatingStopId, setAnimatingStopId] = useState(null);
  const animatingStopIdRef = useRef(null);
  const watchIdRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialCenter = (() => {
    const lat = parseFloat(searchParams.get('lat'));
    const lng = parseFloat(searchParams.get('lng'));
    return !isNaN(lat) && !isNaN(lng) ? [lat, lng] : null;
  })();
  const initialZoom = (() => {
    const z = parseInt(searchParams.get('zoom'), 10);
    return !isNaN(z) ? z : null;
  })();

  const handleViewChange = useCallback((lat, lng, zoom) => {
    setSearchParams(
      { lat: lat.toFixed(5), lng: lng.toFixed(5), zoom: String(zoom) },
      { replace: true }
    );
  }, [setSearchParams]);

  const player = getPlayer() || {};



  const fetchData = useCallback(async () => {
    try {
      const [stopsRes, scoresRes] = await Promise.all([
        stopsApi.getAll(),
        scoreboardApi.get(),
      ]);
      setStops(stopsRes.data);
      setScores(scoresRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Fetch settings (end time + stop radius) and poll every 60s
  useEffect(() => {
    const load = () => {
      settingsApi.get().then(res => {
        setEndTime(res.data.end_time ? new Date(res.data.end_time) : null);
        if (res.data.stop_radius != null) setStopRadius(res.data.stop_radius);
        if (res.data.hide_points != null) setHidePoints(res.data.hide_points);
        if (res.data.hide_scoreboard != null) setHideScoreboard(res.data.hide_scoreboard);
        if (res.data.goal_stop_id != null) setGoalStopId(res.data.goal_stop_id);
      }).catch(() => {});
    };
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  // Countdown tick
  useEffect(() => {
    if (!endTime) { setSecondsLeft(null); return; }
    const tick = () => {
      const diff = Math.max(0, Math.round((endTime - Date.now()) / 1000));
      setSecondsLeft(diff);
    };
    tick();
    const iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }, [endTime]);

  useEffect(() => {
    if (!navigator.geolocation) {
      setGeoStatus('unavailable');
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        setUserPosition({ lat: position.coords.latitude, lng: position.coords.longitude });
        setGeoStatus('granted');
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setGeoStatus('denied');
        } else {
          setGeoStatus('error');
        }
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
    );

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
      }
    };
  }, []);

  const handleStopClick = (stop) => {
    setSelectedStop(stop);
  };

  const handleLogout = () => {
    clearPlayer();
    navigate('/', { replace: true });
  };

  const requestLocationAccess = useCallback(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUserPosition({ lat: position.coords.latitude, lng: position.coords.longitude });
        setGeoStatus('granted');
        watchIdRef.current = navigator.geolocation.watchPosition(
          (pos) => {
            setUserPosition({ lat: pos.coords.latitude, lng: pos.coords.longitude });
            setGeoStatus('granted');
          },
          (err) => {
            if (err.code === err.PERMISSION_DENIED) setGeoStatus('denied');
            else setGeoStatus('error');
          },
          { enableHighAccuracy: true, maximumAge: 10000, timeout: 15000 }
        );
      },
      (err) => {
        if (err.code === err.PERMISSION_DENIED) setGeoStatus('denied');
        else setGeoStatus('error');
      },
      { enableHighAccuracy: true, timeout: 15000 }
    );
  }, []);

  const distanceToSelected = selectedStop && userPosition
    ? haversineMeters(userPosition.lat, userPosition.lng, selectedStop.latitude, selectedStop.longitude)
    : null;

  const canShowChallenge = (selectedStop?.bypass_distance_check || selectedStop?.title?.startsWith('TEST')) ||
    (distanceToSelected !== null && distanceToSelected <= stopRadius + 10);

  const handleShowChallenge = useCallback(() => {
    if (!canShowChallenge || !selectedStop) return;
    const animated = JSON.parse(localStorage.getItem('animated_stops') || '[]');
    if (animated.includes(selectedStop.id) || animated.length >= TIMELINE_LENGTH) {
      navigate(`/stops/${selectedStop.id}`);
    } else {
      animatingStopIdRef.current = selectedStop.id;
      setAnimatingStopId(selectedStop.id);
      setShowAnimation(true);
    }
  }, [canShowChallenge, selectedStop, navigate]);

  const handleAnimationComplete = useCallback(() => {
    const stopId = animatingStopIdRef.current;
    const animated = JSON.parse(localStorage.getItem('animated_stops') || '[]');
    if (stopId && !animated.includes(stopId)) {
      animated.push(stopId);
      localStorage.setItem('animated_stops', JSON.stringify(animated));
    }
    animatingStopIdRef.current = null;
    flushSync(() => {
      setShowAnimation(false);
      setAnimatingStopId(null);
    });
    navigate(`/stops/${stopId}`);
  }, [navigate]);

  if (loading) {
    return (
      <div className="page loading-page">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="page map-page">
      <div className="map-header">
        <div className="header-left">
          <h1>Reykjavík</h1>
          <span className="team-badge">{player.teamName}</span>
        </div>
        {secondsLeft !== null && (
          <div className={`countdown${secondsLeft === 0 ? ' expired' : ''}`}>
            {secondsLeft === 0 ? 'Ferdig!' : formatCountdown(secondsLeft)}
          </div>
        )}
        <div className="header-right">
          <button className="nav-btn" onClick={() => navigate('/challenges')}>
            Utfordringer
          </button>
          <button className="logout-btn" onClick={handleLogout} title="Bytt bruker">
            ✕
          </button>
        </div>
      </div>

      {(geoStatus === 'denied' || geoStatus === 'unavailable') && (
        <div className="location-banner">
          <span className="location-banner-icon">📍</span>
          <span>
            {geoStatus === 'denied'
              ? 'Posisjon er blokkert. Gi tilgang for å låse opp utfordringer.'
              : 'GPS er ikke tilgjengelig i denne nettleseren.'}
          </span>
          {geoStatus === 'denied' && (
            <button className="location-banner-btn" onClick={requestLocationAccess}>
              Gi tilgang
            </button>
          )}
        </div>
      )}

      <div className="map-container">
        <MapView
          stops={stops}
          teamId={player.teamId}
          onStopClick={handleStopClick}
          userPosition={userPosition}
          stopRadius={stopRadius}
          hidePoints={hidePoints}
          initialCenter={initialCenter}
          initialZoom={initialZoom}
          onViewChange={handleViewChange}
          goalStopId={goalStopId}
        />
      </div>

      <div className="scoreboard-container">
        <Scoreboard scores={scores} currentTeamId={player.teamId} stops={stops} hidePoints={hidePoints} hideScoreboard={hideScoreboard} />
      </div>

      {showAnimation && (
        <TimelineAnimation
          onComplete={handleAnimationComplete}
          level={JSON.parse(localStorage.getItem('animated_stops') || '[]').length}
        />
      )}

      {selectedStop && (
        <>
          <div className="stop-sheet-backdrop" onClick={() => setSelectedStop(null)} />
          <div className="stop-sheet">
            <div className="stop-sheet-handle" />
            <button className="stop-sheet-close" onClick={() => setSelectedStop(null)}>✕</button>

            {selectedStop.image_url && (
              <div className="stop-sheet-image">
                <img src={selectedStop.image_url} alt={selectedStop.title} />
              </div>
            )}
            <div className="stop-sheet-meta">
              <span className="stop-sheet-year">{selectedStop.year}</span>
              {selectedStop.walking_tour && (
                <span className="stop-sheet-tour">🚶 Gåtur</span>
              )}
              {selectedStop.completed_by_teams?.includes(player.teamId) && (
                <span className="stop-sheet-done">✓ Fullført</span>
              )}
            </div>
            <h2 className="stop-sheet-title">{selectedStop.title}</h2>
            {selectedStop.description && (
              <p className="stop-sheet-desc">{selectedStop.description}</p>
            )}
            {!hidePoints && (
              <div className="stop-sheet-points">
                {effectivePoints(
                  selectedStop.points,
                  (selectedStop.completed_by_teams || []).filter(id => id !== player.teamId).length
                )} poeng
              </div>
            )}

            <div className="stop-sheet-distance-row">
              {userPosition === null ? (
                <span className="stop-sheet-distance-text loading">Henter posisjon...</span>
              ) : canShowChallenge ? (
                <span className="stop-sheet-distance-text near">Du er nær nok! 🎯</span>
              ) : (
                <span className="stop-sheet-distance-text far">
                  {Math.round(distanceToSelected)}m unna (maks {stopRadius + 10}m)
                </span>
              )}
            </div>

            <button
              className={`stop-sheet-btn${canShowChallenge ? '' : ' disabled'}`}
              onClick={handleShowChallenge}
              disabled={!canShowChallenge}
            >
              {canShowChallenge ? 'Vis utfordring' : 'For langt unna'}
            </button>

            <a
              className="stop-sheet-maps-btn"
              href={`https://www.google.com/maps/dir/?api=1&destination=${selectedStop.latitude},${selectedStop.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              🗺️ Åpne i Google Maps
            </a>
          </div>
        </>
      )}
    </div>
  );
}
